import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import api from '../../services/api';
import { config } from '../../config/config';

// How often the report silently re-fetches in the background.
const AUTO_REFRESH_MS = 60000;

interface HomeDepotCSTRow {
  import_pro: string;
  domestic_pro: string;
  equipment: string;
  deliver_to: string;
  city_state_zip: string;
  partner_carrier: string;
  delivery_appt: string | null;
  confirmed_delivery: string | null;
  revenue: number | null;
  total_expense: number | null;
  carrier_expense: number | null;
  profit: number | null;
  margin: number | null;
}

// Format a date-only value (YYYY-MM-DD, or the date portion of an ISO
// datetime) as M/D/YY without any timezone shifting. String-slicing avoids
// `new Date('YYYY-MM-DD')` being parsed as UTC and displayed a day early.
const formatDate = (dateStr: string | null): string => {
  if (!dateStr || dateStr.startsWith('0000-00-00')) return '-';
  const [y, m, d] = dateStr.slice(0, 10).split('-');
  if (y && m && d) {
    return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
  }
  return dateStr;
};

// Format currency with thousands separators
const formatCurrency = (value: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format margin as a percentage (dashboard_cst.margin is stored as a percent)
const formatPercent = (value: number | null): string => {
  if (value == null) return '-';
  return `${value.toFixed(1)}%`;
};

// Sum a numeric column across the given rows (used by the subtotal footer,
// which passes the currently-filtered rows so the total is filter-responsive).
const sumBy = (
  rows: { original: HomeDepotCSTRow }[],
  key: keyof HomeDepotCSTRow
): number => rows.reduce((acc, r) => acc + (Number(r.original[key]) || 0), 0);

function HomeDepotCSTContent() {
  // Any orders-scope registry field can be added as a column here; the backend
  // attaches it to the rows this report already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('home-depot-cst');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;

  // Data state
  const [data, setData] = useState<HomeDepotCSTRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Order details drawer state — ProDetailSlideOver fetches/refreshes the
  // order itself; we only track which PRO is open.
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  // Fetch data. mode 'initial' shows the spinner and clears the table; mode
  // 'background' (the auto-refresh) swaps data in silently and, on failure,
  // keeps the existing rows on screen rather than blanking the table.
  const fetchData = useCallback(async (mode: 'initial' | 'background' = 'background') => {
    if (!isMounted.current) return;
    const initial = mode === 'initial';

    // Cancel any in-flight request to prevent stale responses
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (initial) {
        setError(null);
        setData([]);
        setLoading(true);
      }

      // The saved selection is part of the request; going early just fetches twice.
      if (!fieldsReady) return;
      const response = await api.get('/reporting/home-depot-cst', {
        params: {
          // A timer-driven refresh is the page keeping itself current, not somebody
          // asking for the report, so it is not recorded as a run.
          ...(initial ? {} : { background: true }),
          ...(additionalFieldsKey ? { additional_fields: additionalFieldsKey } : {}),
        },
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
      setError(null);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      // Only surface errors on the initial load; a failed background refresh
      // leaves the last good data in place.
      if (initial) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
    } finally {
      if (isMounted.current && !controller.signal.aborted && initial) {
        setLoading(false);
      }
    }
  }, [fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    fetchData('initial');
  }, [fetchData]);

  // Auto-refresh: silently re-fetch on an interval. Pauses while the tab is
  // hidden and fires immediately when it becomes visible again.
  useVisibilityAwareInterval(() => {
    fetchData('background');
  }, AUTO_REFRESH_MS);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Clickable PRO cell — opens the pro drawer and marks the PRO viewed, with a
  // secondary link out to CMSV1. Shared by the Import Pro and Domestic Pro columns.
  const renderProCell = useCallback((pronumber: string) => {
    if (!pronumber) return <span className="text-gray-400">-</span>;
    // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
    // trackViewedColumnId wrapper on the pro columns.
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openOrderDrawer(Number(pronumber));
          }}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        >
          {pronumber}
        </button>
        <a
          href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Open in CMSV1"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
      </div>
    );
  }, [openOrderDrawer]);

  // Column definitions
  const baseColumns = useMemo<ColumnDef<HomeDepotCSTRow>[]>(() => [
    {
      accessorKey: 'import_pro',
      header: 'Import Pro',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.import_pro),
      footer: () => <span className="font-semibold">Totals</span>,
    },
    {
      accessorKey: 'domestic_pro',
      header: 'Domestic Pro',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.domestic_pro),
    },
    {
      accessorKey: 'equipment',
      header: 'Equipment #',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'deliver_to',
      header: 'Deliver To',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'city_state_zip',
      header: 'City State, Zip',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'partner_carrier',
      header: 'Partner Carrier',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'delivery_appt',
      header: 'Delivery Appt',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.delivery_appt),
    },
    {
      accessorKey: 'confirmed_delivery',
      header: 'Confirmed Delivery',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.confirmed_delivery),
    },
    {
      accessorKey: 'revenue',
      header: 'Revenue',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.revenue),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'revenue'))}
        </span>
      ),
    },
    {
      accessorKey: 'total_expense',
      header: 'Total Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.total_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'total_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'carrier_expense',
      header: 'Carrier Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.carrier_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'carrier_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'profit',
      header: 'Profit',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.profit),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'profit'))}
        </span>
      ),
    },
    {
      accessorKey: 'margin',
      header: 'Margin',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatPercent(row.original.margin),
      // Blended margin across the filtered rows: total profit / total revenue.
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows;
        const revenue = sumBy(rows, 'revenue');
        return (
          <span className="font-semibold">
            {revenue !== 0 ? formatPercent((sumBy(rows, 'profit') / revenue) * 100) : '-'}
          </span>
        );
      },
    },
  ], [renderProCell]);

  // Rows are report rows, not orders — several can share a pro — so an added order
  // column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<HomeDepotCSTRow>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: HomeDepotCSTRow) => {
    return `${row.domestic_pro}|${row.import_pro}`;
  }, []);

  const infoBanner = (
    <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      Domestic brokerage orders billed to Home Depot, paired with their import PRO and CST
      dashboard financials (revenue, expense, and margin). Sorted by delivery appointment.
      The subtotal row reflects the current filters. Auto-refreshes every 60 seconds.
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {infoBanner}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {infoBanner}

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId={['import_pro', 'domestic_pro']}
          loading={loading}
          defaultSorting={[{ id: 'delivery_appt', desc: false }]}
          settingsKey="home-depot-cst"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="Home Depot - CST"
          fillContainer={true}
          showColumnFooter={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function HomeDepotCST() {
  return (
    <ViewedProsProvider>
      <HomeDepotCSTContent />
    </ViewedProsProvider>
  );
}

export default HomeDepotCST;
